/**
 * Route Guard Components
 *
 * Provides authentication-aware route protection:
 * - ProtectedRoute: Requires authentication, redirects to login if not authenticated
 * - AuthRoute: For non-authenticated users only (login, register), redirects to home if authenticated
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions, type ModuleId } from '../../contexts/PermissionsContext';
import { useTranslation } from '../../hooks';

// =============================================================================
// LOADING SPINNER
// =============================================================================

function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '1rem',
      backgroundColor: 'var(--bg-primary, #ffffff)',
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid var(--border, #e5e7eb)',
        borderTopColor: 'var(--primary, #3b82f6)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{ color: 'var(--text-muted, #6b7280)' }}>{t('auth.verifyingAuth')}</p>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// PROTECTED ROUTE
// =============================================================================

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedRoute - Requires authentication to access
 *
 * - Shows loading spinner while checking auth status
 * - Redirects to login page if not authenticated
 * - Renders children if authenticated
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ children, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render protected content
  return <>{children}</>;
}

// =============================================================================
// AUTH ROUTE
// =============================================================================

interface AuthRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * AuthRoute - For non-authenticated users only
 *
 * - Shows loading spinner while checking auth status
 * - Redirects to home/dashboard if already authenticated
 * - Renders children (login/register pages) if not authenticated
 *
 * Usage:
 * ```tsx
 * <AuthRoute>
 *   <LoginPage />
 * </AuthRoute>
 * ```
 */
export function AuthRoute({ children, redirectTo = '/home' }: AuthRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to home if already authenticated
  if (isAuthenticated) {
    // Check if there's a saved location to redirect to
    const from = (location.state as { from?: Location })?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  // Render auth content (login, register, etc.)
  return <>{children}</>;
}

// =============================================================================
// ROLE-BASED ROUTE
// =============================================================================

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRole: string | string[];
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * RoleProtectedRoute - Requires specific role to access
 *
 * - Checks if user has required role
 * - Redirects or shows fallback if role doesn't match
 *
 * Usage:
 * ```tsx
 * <RoleProtectedRoute requiredRole="admin">
 *   <AdminPanel />
 * </RoleProtectedRoute>
 * ```
 */
export function RoleProtectedRoute({
  children,
  requiredRole,
  redirectTo = '/home',
  fallback,
}: RoleProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check role
  const userRole = user.role;
  const hasRole = Array.isArray(requiredRole)
    ? requiredRole.includes(userRole)
    : userRole === requiredRole;

  if (!hasRole) {
    // Show fallback if provided, otherwise redirect
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Render protected content
  return <>{children}</>;
}

// =============================================================================
// MODULE-BASED ROUTE
// =============================================================================

interface ModuleRouteProps {
  children: ReactNode;
  moduleId: ModuleId;
  requiredLevel?: 'access' | 'edit' | 'admin';
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * ModuleRoute - Requires specific module access permission
 *
 * - Checks user permissions for the specified module
 * - Supports different permission levels: access, edit, admin
 * - Shows fallback or redirects if no permission
 *
 * Usage:
 * ```tsx
 * <ModuleRoute moduleId="contabilita" requiredLevel="access">
 *   <AccountingModule />
 * </ModuleRoute>
 * ```
 */
export function ModuleRoute({
  children,
  moduleId,
  requiredLevel = 'access',
  fallback,
  redirectTo = '/home',
}: ModuleRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { canAccess, canEdit, canAdmin } = usePermissions();
  const location = useLocation();

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission level
  let hasPermission = false;
  switch (requiredLevel) {
    case 'admin':
      hasPermission = canAdmin(moduleId);
      break;
    case 'edit':
      hasPermission = canEdit(moduleId);
      break;
    case 'access':
    default:
      hasPermission = canAccess(moduleId);
      break;
  }

  if (!hasPermission) {
    // Show fallback if provided, otherwise redirect
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Render protected content
  return <>{children}</>;
}

// =============================================================================
// ACCESS DENIED COMPONENT
// =============================================================================

interface AccessDeniedProps {
  title?: string;
  message?: string;
  showBackButton?: boolean;
  /** Resource type for access request */
  resourceType?: string;
  /** Resource ID for access request */
  resourceId?: string;
  /** Action being requested */
  action?: string;
  /** Whether user can request access (auto-detected if not provided) */
  canRequest?: boolean;
}

/**
 * AccessDenied - Fallback component for unauthorized access
 * Enhanced with "Request Access" button for team members
 */
export function AccessDenied({
  title,
  message,
  showBackButton = true,
  resourceType,
  resourceId,
  action = 'view',
  canRequest: canRequestProp,
}: AccessDeniedProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const displayTitle = title ?? t('auth.accessDenied');
  const displayMessage = message ?? t('auth.accessDeniedMessage');

  const [requestState, setRequestState] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // Determine if user can request access
  const canRequest = canRequestProp ?? (
    user &&
    ((user as any).user_type === 'team_member' || user.role === 'user') &&
    resourceType !== undefined
  );

  const handleRequestAccess = async () => {
    if (!resourceType) return;

    setRequestState('loading');
    setErrorMessage(null);

    try {
      const { graphqlMutation } = await import('@business-assistant/shared/database/graphqlClient');

      const response = await graphqlMutation<{
        requestAccess: { id: string } | null;
      }>(`
        mutation RequestAccess($input: RequestAccessInput!) {
          requestAccess(input: $input) {
            id
          }
        }
      `, {
        input: {
          resourceType,
          resourceId,
          action,
        },
      });

      if (response.requestAccess) {
        setRequestState('success');
      } else {
        throw new Error('Failed to create access request');
      }
    } catch (err) {
      console.error('[AccessDenied] Request access error:', err);
      setRequestState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '1.5rem',
      backgroundColor: 'var(--bg-primary, #ffffff)',
      padding: '2rem',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: requestState === 'success'
          ? 'var(--success-bg, #f0fdf4)'
          : 'var(--error-bg, #fef2f2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {requestState === 'success' ? (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--success, #22c55e)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        ) : (
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--error, #ef4444)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        )}
      </div>

      <h2 style={{
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: 600,
        color: 'var(--text-primary, #111827)',
      }}>
        {requestState === 'success'
          ? t('auth.accessRequestSent', 'Access Request Sent')
          : displayTitle}
      </h2>

      <p style={{
        margin: 0,
        fontSize: '1rem',
        color: 'var(--text-muted, #6b7280)',
        textAlign: 'center',
        maxWidth: '400px',
      }}>
        {requestState === 'success'
          ? t('auth.accessRequestSentMessage', 'Your request has been sent to the administrator. You will be notified when it is reviewed.')
          : displayMessage}
      </p>

      {errorMessage && (
        <p style={{
          margin: 0,
          fontSize: '0.875rem',
          color: 'var(--error, #ef4444)',
          textAlign: 'center',
        }}>
          {errorMessage}
        </p>
      )}

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginTop: '0.5rem',
      }}>
        {showBackButton && (
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: requestState === 'success' ? '#ffffff' : 'var(--text-primary, #111827)',
              backgroundColor: requestState === 'success' ? 'var(--primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
            }}
          >
            {t('auth.goBack', 'Go Back')}
          </button>
        )}

        {canRequest && requestState !== 'success' && (
          <button
            onClick={handleRequestAccess}
            disabled={requestState === 'loading'}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: requestState === 'loading'
                ? 'var(--primary-muted, #93c5fd)'
                : 'var(--primary, #3b82f6)',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: requestState === 'loading' ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {requestState === 'loading' ? (
              <>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                {t('common.loading', 'Loading...')}
              </>
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
                {t('auth.requestAccess', 'Request Access')}
              </>
            )}
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  ProtectedRoute,
  AuthRoute,
  RoleProtectedRoute,
  ModuleRoute,
  AccessDenied,
};
